import React, { useState, useEffect, useRef } from "react";
import "../assets/Newnavbar.css";

import logo from '../assets/imgs/samriddhi_group_white_logo-removebg-preview.png';

const menuItems = [
    {
        label: "About Us",
        type: "mega",
        columns: [
            {
                heading: "Discover Samriddhi",
                links: [
                    { label: "Our Story", path: "/about-us/our-story" },
                    { label: "Our Vision & Mission", path: "/about-us/vision-mission" },
                    { label: "Leadership", path: "/about-us/leadership" },
                ],
            },
            {
                heading: "Our Community",
                links: [
                    { label: "Our Customers", path: "/our-customers" },
                    { label: "Life at Samriddhi", path: "/life-at-samriddhi" },
                ],
            },
        ],
    },
    {
        label: "Projects",
        type: "mega",
        columns: [
            {
                heading: "Nature & Lifestyle",
                links: [
                    { label: "Lonavalley", path: "/projects/lonavalley" },
                    { label: "Waterscape", path: "/projects/waterscape" },
                    { label: "Riviera Greens", path: "/projects/riviera-greens" },
                    { label: "Kokan Samriddhi", path: "/projects/kokan-samriddhi" },
                ],
            },
            {
                heading: "Residential & Investment",
                links: [
                    { label: "Blue Horizon", path: "/projects/blue-horizon" },
                    { label: "Dhayari Residences", path: "/projects/dhayari" },
                    { label: "Aatmabodh Vatika", path: "/projects/aatmabodh-vatika" },
                    { label: "View All Projects", path: "/projects" },
                ],
            },
        ],
    },
    {
        label: "Our Customers",
        type: "link",
        path: "/our-customers",
    },
    {
        label: "Careers",
        type: "link",
        path: "/careers",
    },
    {
        label: "Contact Us",
        type: "button",
        path: "/contact",
    },
];

export default function Navbar() {
    const [activeMenu, setActiveMenu] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileExpandedMenu, setMobileExpandedMenu] = useState(null);
    const navbarRef = useRef(null);
    const closeTimerRef = useRef(null);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                navbarRef.current &&
                !navbarRef.current.contains(event.target)
            ) {
                setActiveMenu(null);
                setMobileMenuOpen(false);
                setMobileExpandedMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Close menus on Escape
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setActiveMenu(null);
                setMobileMenuOpen(false);
                setMobileExpandedMenu(null);
            }
        };

        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, []);

    // Prevent background scrolling when mobile menu is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.classList.add("navbar-menu-open");
        } else {
            document.body.classList.remove("navbar-menu-open");
        }

        return () => {
            document.body.classList.remove("navbar-menu-open");
        };
    }, [mobileMenuOpen]);

    // Clean up pending timers
    useEffect(() => {
        return () => clearTimeout(closeTimerRef.current);
    }, []);

    const handleMouseEnter = (label) => {
        clearTimeout(closeTimerRef.current);
        setActiveMenu(label);
    };

    const handleMouseLeave = () => {
        closeTimerRef.current = setTimeout(() => {
            setActiveMenu(null);
        }, 180);
    };

    const toggleMobileSubmenu = (label) => {
        setMobileExpandedMenu((current) =>
            current === label ? null : label
        );
    };

    const closeAllMenus = () => {
        setActiveMenu(null);
        setMobileMenuOpen(false);
        setMobileExpandedMenu(null);
    };

    return (
        <header className="sam-navbar" ref={navbarRef}>
            <div className="sam-navbar-main">
                {/* Logo */}
                <a
                    href="/"
                    className="sam-navbar-logo"
                    aria-label="Samriddhi Group Home"
                    onClick={closeAllMenus}
                >
                    <img
                        src={logo}
                        alt="Samriddhi Group"
                    />
                </a>

                {/* Desktop Navigation */}
                <nav className="sam-navbar-desktop" aria-label="Main navigation">
                    {menuItems.map((item) => {
                        const isMega = item.type === "mega";
                        const isActive = activeMenu === item.label;

                        return (
                            <div
                                key={item.label}
                                className={`sam-nav-item ${isActive ? "sam-nav-item-active" : ""
                                    }`}
                                onMouseEnter={() => {
                                    if (isMega) handleMouseEnter(item.label);
                                }}
                                onMouseLeave={() => {
                                    if (isMega) handleMouseLeave();
                                }}
                                onFocus={() => {
                                    if (isMega) handleMouseEnter(item.label);
                                }}
                            >
                                {isMega ? (
                                    <button
                                        type="button"
                                        className="sam-nav-link sam-nav-trigger"
                                        aria-expanded={isActive}
                                        aria-haspopup="true"
                                        onClick={() =>
                                            setActiveMenu(isActive ? null : item.label)
                                        }
                                    >
                                        {item.label}
                                        <span className="sam-nav-chevron" aria-hidden="true">
                                            ⌄
                                        </span>
                                    </button>
                                ) : (
                                    <a
                                        href={item.path}
                                        className={`sam-nav-link ${item.type === "button" ? "sam-nav-contact" : ""
                                            }`}
                                        onClick={closeAllMenus}
                                    >
                                        {item.label}
                                    </a>
                                )}
                            </div>
                        );
                    })}
                </nav>

                {/* Desktop Menu Icon */}
                <button
                    type="button"
                    className="sam-navbar-menu-icon"
                    aria-label="Open navigation menu"
                    aria-expanded={activeMenu === "menu"}
                    onMouseEnter={() => handleMouseEnter("menu")}
                    onMouseLeave={handleMouseLeave}
                    onClick={() =>
                        setActiveMenu(activeMenu === "menu" ? null : "menu")
                    }
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>

                {/* Mobile Menu Toggle */}
                <button
                    type="button"
                    className={`sam-navbar-mobile-toggle ${mobileMenuOpen ? "is-open" : ""
                        }`}
                    aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                    aria-expanded={mobileMenuOpen}
                    onClick={() => setMobileMenuOpen((open) => !open)}
                >
                    <span></span>
                    <span></span>
                </button>
            </div>

            {/* Desktop Mega Menu */}
            <div
                className={`sam-mega-menu ${activeMenu && activeMenu !== "menu" ? "is-visible" : ""
                    }`}
                onMouseEnter={() => clearTimeout(closeTimerRef.current)}
                onMouseLeave={handleMouseLeave}
            >
                {menuItems
                    .filter(
                        (item) =>
                            item.type === "mega" && item.label === activeMenu
                    )
                    .map((item) => (
                        <div className="sam-mega-inner" key={item.label}>
                            <div className="sam-mega-heading">
                                <span className="sam-mega-eyebrow">
                                    EXPLORE SAMRIDDHI
                                </span>
                                <h2>{item.label}</h2>
                                <p>
                                    Discover thoughtfully planned communities and
                                    experiences designed around the way you live.
                                </p>
                            </div>

                            <div className="sam-mega-columns">
                                {item.columns.map((column) => (
                                    <div className="sam-mega-column" key={column.heading}>
                                        <h3>{column.heading}</h3>

                                        {column.links.map((link) => (
                                            <a
                                                href={link.path}
                                                className="sam-mega-link"
                                                key={link.label}
                                                onClick={closeAllMenus}
                                            >
                                                <span>{link.label}</span>
                                                <span className="sam-mega-arrow">↗</span>
                                            </a>
                                        ))}
                                    </div>
                                ))}
                            </div>

                            <div className="sam-mega-footer">
                                <span>BEYOND REAL ESTATE</span>
                                <span className="sam-mega-footer-line"></span>
                                <span>SAMRIDDHI GROUP</span>
                            </div>
                        </div>
                    ))}
            </div>

            {/* Desktop Hamburger Mega Menu */}
            <div
                className={`sam-menu-panel ${activeMenu === "menu" ? "is-visible" : ""
                    }`}
                onMouseEnter={() => clearTimeout(closeTimerRef.current)}
                onMouseLeave={handleMouseLeave}
            >
                <div className="sam-menu-panel-inner">
                    <span className="sam-mega-eyebrow">QUICK NAVIGATION</span>

                    {menuItems.map((item) => (
                        <a
                            href={item.path || "#"}
                            className="sam-menu-panel-link"
                            key={item.label}
                            onClick={closeAllMenus}
                        >
                            {item.label}
                            <span>↗</span>
                        </a>
                    ))}
                </div>
            </div>

            {/* Mobile Navigation */}
            <div
                className={`sam-mobile-menu ${mobileMenuOpen ? "is-open" : ""
                    }`}
                aria-hidden={!mobileMenuOpen}
            >
                <div className="sam-mobile-menu-inner">
                    <span className="sam-mega-eyebrow">MENU</span>

                    {menuItems.map((item) => {
                        const isMega = item.type === "mega";
                        const isExpanded = mobileExpandedMenu === item.label;

                        return (
                            <div className="sam-mobile-item" key={item.label}>
                                {isMega ? (
                                    <>
                                        <button
                                            type="button"
                                            className="sam-mobile-link sam-mobile-trigger"
                                            aria-expanded={isExpanded}
                                            onClick={() => toggleMobileSubmenu(item.label)}
                                            tabIndex={mobileMenuOpen ? 0 : -1}
                                        >
                                            {item.label}
                                            <span
                                                className={`sam-mobile-chevron ${isExpanded ? "is-expanded" : ""
                                                    }`}
                                            >
                                                +
                                            </span>
                                        </button>

                                        <div
                                            className={`sam-mobile-submenu ${isExpanded ? "is-expanded" : ""
                                                }`}
                                        >
                                            {item.columns.map((column) => (
                                                <div
                                                    className="sam-mobile-submenu-column"
                                                    key={column.heading}
                                                >
                                                    <h3>{column.heading}</h3>

                                                    {column.links.map((link) => (
                                                        <a
                                                            href={link.path}
                                                            key={link.label}
                                                            onClick={closeAllMenus}
                                                            tabIndex={
                                                                mobileMenuOpen && isExpanded ? 0 : -1
                                                            }
                                                        >
                                                            {link.label}
                                                        </a>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <a
                                        href={item.path}
                                        className={`sam-mobile-link ${item.type === "button"
                                            ? "sam-mobile-contact"
                                            : ""
                                            }`}
                                        onClick={closeAllMenus}
                                        tabIndex={mobileMenuOpen ? 0 : -1}
                                    >
                                        {item.label}
                                        <span>↗</span>
                                    </a>
                                )}
                            </div>
                        );
                    })}

                    <div className="sam-mobile-footer">
                        <span>BEYOND REAL ESTATE</span>
                        <span>SAMRIDDHI GROUP</span>
                    </div>
                </div>
            </div>
        </header>
    );
}